import React, { useEffect } from "react";

import "./themes/styles.css";
import "./fonts/fonts.css";
import "@mantine/core/styles.css";
import "@mantine/spotlight/styles.css";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/nprogress/styles.css";
import "@mantine/code-highlight/styles.css";

import { AppShell, MantineProvider } from "@mantine/core";
import { NavigationProgress } from "@mantine/nprogress";
import { ModalsProvider } from "@mantine/modals";
import { CodeHighlightAdapterProvider, createShikiAdapter } from "@mantine/code-highlight";

import { useDisclosure } from "@mantine/hooks";
import { Notifications } from "@mantine/notifications";
import { CssVariableResolver } from "./themes/cssVariableResolver";
// import { useHeadroom } from "@mantine/hooks";

import { useAtom } from "jotai";
import { userAtom } from "#root/atoms/userAtom";
import { themeAtom } from "#root/layouts/themes/theme-atom";
import { getDynamicTheme } from "./themes/theme";

import Affix from "#root/components/Affix";
import FooterLinks from "#root/components/Footer-with-links/FooterWithLinks";
import HeaderContent from "#root/components/HeaderContent";
import NavbarLinks from "#root/components/NavbarLinks/NavbarLinks";

import { usePageContext } from "vike-react/usePageContext";

export default function LayoutDefault({ children }: { children: React.ReactNode }) {
  const [prefs] = useAtom(themeAtom);
  const [opened, { toggle }] = useDisclosure();
  // to make the header collapse when scrolling down
  // const pinned = useHeadroom({ fixedAt: 120 });
  // header={{ height: 60, collapsed: !pinned, offset: true }}

  const pageContext = usePageContext();
  const [user, setUser] = useAtom(userAtom);

  useEffect(() => {
    setUser(pageContext.user ?? null);
  }, []);

  // Shiki requires async code to load the highlighter
  async function loadShiki() {
    const { createHighlighter } = await import("shiki");
    const shiki = await createHighlighter({
      langs: ["tsx", "scss", "html", "bash", "json", "markdown", "toml", "ts"],
      themes: ["vesper"],
    });

    return shiki;
  }

  const shikiAdapter = createShikiAdapter(loadShiki);

  return (
    <>
      <MantineProvider
        theme={getDynamicTheme(prefs)}
        defaultColorScheme="dark"
        cssVariablesResolver={CssVariableResolver}
      >
        <CodeHighlightAdapterProvider adapter={shikiAdapter}>
          <NavigationProgress withinPortal={true} aria-label="Page is loading..." />
          <Notifications />
          <Affix />
          <ModalsProvider labels={{ confirm: "Submit", cancel: "Cancel" }}>
            <AppShell
              offsetScrollbars={true}
              transitionDuration={500}
              transitionTimingFunction="ease"
              header={{ height: 60, collapsed: false, offset: true }}
              navbar={{ width: 300, breakpoint: "sm", collapsed: { mobile: !opened, desktop: !opened } }}
              p="md"
            >
              <HeaderContent opened={opened} toggle={toggle} />
              <NavbarLinks toggle={toggle} />
              <AppShell.Main> {children}</AppShell.Main>
            </AppShell>
            <FooterLinks />
          </ModalsProvider>
        </CodeHighlightAdapterProvider>
      </MantineProvider>
    </>
  );
}
